import './style.css';
import { Fragment, FunctionalComponent, h } from 'preact';
import { useEffect, useRef, useState } from 'preact/hooks';
import type { PagePreviewState, ProductQuery, SessionProduct } from './mock/types';
import { ANCHORS } from './mock/data';
import { fetchRecommend, CategoryOption } from './dataSource';
import { OfferPrice } from './components/ProductCard';
import TabBar from './components/TabBar';
import DetailModal from './components/DetailModal';
import { EmptyState, SkeletonList } from './state';
import { withGhId } from './apiConfig';
import ScrollLoader from './components/ScrollLoader';
import { usePageView } from './usePageView';

/** 直播精选（F01–F04）：运营推荐固定顺序，始终在全部主播的推荐里筛选 */
const TrailerFeatured: FunctionalComponent = () => {
  usePageView('featured');
  const [query, setQuery] = useState<ProductQuery>({});
  const [preview, setPreview] = useState<PagePreviewState>('normal');
  const [detailId, setDetailId] = useState<string | null>(null);

  const [all, setAll] = useState<SessionProduct[]>([]);
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const category = query.categoryId ?? 'all';
  const sortType = query.priceSort === 'asc' ? 'price_asc' : query.priceSort === 'desc' ? 'price_desc' : 'default';
  const requestKey = `${category}|${sortType}`;
  const [settledKey, setSettledKey] = useState('');
  const generation = useRef(0);
  const moreBusy = useRef(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    const current = ++generation.current;
    let stale = false;
    setLoading(true);
    setFailed(false);
    setPage(1);
    setLoadingMore(false);
    moreBusy.current = false;
    setMoreFailed(false);
    fetchRecommend({ categoryId: category, sortType, page: 1 })
      .then((ret) => {
        if (stale) return;
        const nextCategories = ret.categories ?? [];
        setCategories(nextCategories);
        if (category !== 'all' && !nextCategories.some((c) => c.id === category)) {
          setQuery((previous) => ({ ...previous, categoryId: 'all' }));
          return;
        }
        setAll(ret.list); setTotal(ret.total); setHasMore(ret.hasMore);
      })
      .catch(() => { if (!stale) setFailed(true); })
      .finally(() => { if (!stale) { setSettledKey(requestKey); setLoading(false); } });
    return () => { stale = true; if (generation.current === current) generation.current++; };
  }, [category, sortType, retry]);
  const detailRec = detailId ? all.find((r) => r.sessionProductId === detailId) : null;
  const list = all;
  const loadMore = async () => {
    if (moreBusy.current || loading || !hasMore || settledKey !== requestKey) return;
    moreBusy.current = true;
    const current = generation.current;
    setLoadingMore(true);
    setMoreFailed(false);
    try {
      const ret = await fetchRecommend({ categoryId: category, sortType, page: page + 1 });
      if (current !== generation.current) return;
      setAll((previous) => [...previous, ...ret.list.filter((r) => !previous.some((p) => p.sessionProductId === r.sessionProductId))]);
      setPage(page + 1);
      setTotal(ret.total);
      setHasMore(ret.hasMore);
    } catch (_) {
      if (current === generation.current) setMoreFailed(true);
    } finally {
      if (current === generation.current) { setLoadingMore(false); moreBusy.current = false; }
    }
  };

  return (
    <div class="vp-page">

      <div class="vp-topbar vp-page-titlebar">
        <h1>直播精选</h1>
        <button class="vp-global-search" onClick={() => (location.href = withGhId('/live/search'))}><i class="search-ico" />全站搜索</button>
      </div>
      <div class="vp-featured-intro">
        <b>值得一看的直播好物</b>
        <p>直播间推荐好物 · 持续更新</p>
      </div>

      <div class="vp-list-toolbar">
        <div class="vp-category">
          {[{ id: 'all', label: '全部', count: 0 }, ...categories].map((c) => (
            <button
              key={c.id}
              class={`vp-category-item${(query.categoryId ?? 'all') === c.id ? ' selected' : ''}`}
              onClick={() => setQuery({ ...query, categoryId: c.id })}
            >
              {c.label}
            </button>
          ))}
        </div>
        <div class="vp-sortbar vp-featured-sort">
          <button class={`vp-sort-item${!query.priceSort ? ' selected' : ''}`} onClick={() => setQuery({ ...query, priceSort: null })}>默认</button>
          <button
            class={`vp-sort-item${query.priceSort ? ' selected' : ''}`}
            onClick={() => setQuery({ ...query, priceSort: query.priceSort === 'asc' ? 'desc' : 'asc' })}
          >
            价格
            <span class="vp-sort-arrow">
              <i class={`up${query.priceSort === 'asc' ? ' on' : ''}`} />
              <i class={`down${query.priceSort === 'desc' ? ' on' : ''}`} />
            </span>
          </button>
          <div class="vp-sort-note">精选推荐 · 持续更新</div>
        </div>
      </div>

      {preview === 'loading' || loading || settledKey !== requestKey ? (
        <SkeletonList />
      ) : preview === 'failed' || failed ? (
        <EmptyState
          spec={{ title: '精选暂时加载失败', actions: [{ label: '重新加载', kind: 'reload' }] }}
          onAction={() => { setPreview('normal'); setRetry((n) => n + 1); }}
        />
      ) : preview === 'empty' ? (
        <EmptyState
          spec={{ title: '精选好物正在整理中', actions: [{ label: '查看直播日历', kind: 'calendar' }] }}
          onAction={() => (location.href = withGhId('/live/calendar'))}
        />
      ) : preview === 'expired' ? (
        <EmptyState
          spec={{ title: '该推荐已结束，看看其他直播好物吧', actions: [{ label: '刷新推荐', kind: 'reload' }] }}
          onAction={() => { setPreview('normal'); setRetry((n) => n + 1); }}
        />
      ) : list.length === 0 ? (
        <EmptyState spec={{ title: '暂无符合条件的推荐', actions: [{ label: '查看全部推荐', kind: 'clear' }] }} onAction={() => setQuery({})} />
      ) : (
        <>
          <div class="vp-metarow"><span>共 {total} 件推荐商品</span><span>持续更新</span></div>
          {list.map((rec) => <RecCard key={rec.sessionProductId} rec={rec} onOpenDetail={() => setDetailId(rec.sessionProductId)} />)}
          <ScrollLoader hasMore={hasMore} loading={loadingMore} failed={moreFailed}
            loaded={list.length} total={total} onLoad={loadMore} endText="已展示全部推荐商品" />
        </>
      )}

      <div class="vp-tab-content-pad" />
      <TabBar active="featured" />

      <DetailModal
        product={detailRec ?? null}
        anchorName=""
        onClose={() => setDetailId(null)}
        onViewSession={(product) => { location.href = withGhId(`/live?anchor=${product.session.anchorId}&date=${product.session.date}&session=${product.session.id}`); }}
      />
    </div>
  );
};

/** 推荐卡：纯商品列表（精选不展示直播间/主题信息） */
function RecCard({ rec, onOpenDetail }: { rec: SessionProduct; onOpenDetail: () => void }) {
  const sourceAnchor = ANCHORS.find((a) => a.id === rec.session.anchorId);
  const s0 = rec.offers.reduce<typeof rec.offers[number] | undefined>((best, offer) => {
    if (offer.price === null) return best;
    return !best || Number(offer.price) < Number(best.price) ? offer : best;
  }, undefined) ?? rec.offers[0];
  return (
    <div class="vp-productrow">
      <button class="vp-productcard-figure" onClick={onOpenDetail}>
        <img src={rec.image} alt={rec.itemName} />
        {rec.linkNo > 0 && <span class="vp-linkno">{rec.linkNo}号</span>}
      </button>
      <div class="vp-productrow-body">
        <span class="vp-rec-origin">{sourceAnchor ? `${sourceAnchor.name} · ` : ''}{rec.session.date} {rec.session.startTime}</span>
        <button class="vp-productrow-title vp-productcard-title" onClick={onOpenDetail}>{rec.itemName}</button>
        <div class="vp-price-row">
          <OfferPrice price={s0?.price ?? null} unit={s0?.toHandUnit ?? ''} />
        </div>
        <button class="vp-card-btn compact solid" onClick={onOpenDetail}>看详情</button>
      </div>
    </div>
  );
}

export default TrailerFeatured;
